// src/features/admin/services/adminProductService.js
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  writeBatch
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../../../services/firebase';

const COLLECTION_NAME = 'october_products';

export class AdminProductService {
  // Upload image to Firebase Storage
  static async uploadImage(file, productId = null) {
    try {
      // Validate file
      if (!file) {
        throw new Error('No file provided');
      }

      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        throw new Error(`Invalid file type: ${file.type}. Allowed types: ${validTypes.join(', ')}`);
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        throw new Error(`File size too large: ${(file.size / 1024 / 1024).toFixed(2)}MB. Maximum size: 10MB`);
      }

      const timestamp = Date.now();
      // Sanitize filename
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const fileName = `${timestamp}_${sanitizedFileName}`;
      const storageRef = ref(storage, `products/${fileName}`);
      
      // Set metadata for better handling
      const metadata = {
        contentType: file.type,
        customMetadata: {
          uploadedAt: timestamp.toString(),
          originalName: file.name
        }
      };

      const snapshot = await uploadBytes(storageRef, file, metadata);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        fileName: fileName,
        path: snapshot.ref.fullPath
      };
    } catch (error) {
      console.error('Error uploading image:', error);
      
      // Provide more detailed error messages
      if (error.code === 'storage/unauthorized') {
        throw new Error('Permission denied. Please check Firebase Storage security rules.');
      } else if (error.code === 'storage/canceled') {
        throw new Error('Upload was canceled.');
      } else if (error.code === 'storage/unknown') {
        throw new Error(`Storage error: ${error.message || 'Unknown error occurred. Please check Firebase Storage configuration and security rules.'}`);
      } else if (error.code === 'storage/quota-exceeded') {
        throw new Error('Storage quota exceeded. Please contact administrator.');
      } else if (error.code === 'storage/unauthenticated') {
        throw new Error('Authentication required. Please sign in to continue.');
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error(`Failed to upload image: ${error.code || 'Unknown error'}`);
      }
    }
  }

  // Delete image from Firebase Storage
  static async deleteImage(imagePath) {
    try {
      const imageRef = ref(storage, imagePath);
      await deleteObject(imageRef);
      return true;
    } catch (error) {
      console.error('Error deleting image:', error);
      // Don't throw error if image doesn't exist
      return false;
    }
  }

  // Get all products for admin (with additional fields)
  static async getAllProductsAdmin() {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const products = [];
      
      querySnapshot.forEach((doc) => {
        products.push({ 
          id: doc.id, 
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
        });
      });
      
      return products;
    } catch (error) {
      console.error('Error fetching products for admin:', error);
      throw error;
    }
  }

  // Add new product
  static async addProduct(productData, imageFile = null) {
    try {
      let imageData = null;
      
      // Upload image if provided
      if (imageFile) {
        imageData = await this.uploadImage(imageFile);
      }

      const newProduct = {
        ...productData,
        image: imageData ? imageData.url : productData.image || '',
        imagePath: imageData ? imageData.path : '',
        priceValue: parseFloat(productData.priceValue) || 0,
        rating: parseFloat(productData.rating) || 4.5,
        reviews: parseInt(productData.reviews) || 0,
        inStock: productData.inStock !== undefined ? productData.inStock : true,
        featured: productData.featured || false,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, COLLECTION_NAME), newProduct);
      
      return { 
        id: docRef.id, 
        ...newProduct 
      };
    } catch (error) {
      console.error('Error adding product:', error);
      
      // Provide more detailed error messages
      if (error.code === 'permission-denied') {
        throw new Error('Permission denied. Please sign in as an admin and ensure Firestore security rules are configured correctly.');
      } else if (error.code === 'unavailable') {
        throw new Error('Firestore service is temporarily unavailable. Please try again later.');
      } else if (error.message) {
        throw new Error(`Failed to add product: ${error.message}`);
      } else {
        throw new Error(`Failed to add product: ${error.code || 'Unknown error'}`);
      }
    }
  }

  // Update existing product
  static async updateProduct(productId, productData, imageFile = null) {
    try {
      const docRef = doc(db, COLLECTION_NAME, productId);
      const existingDoc = await getDoc(docRef);
      
      if (!existingDoc.exists()) {
        throw new Error('Product not found');
      }

      const existingData = existingDoc.data();
      let imageData = null;

      // Handle image update
      if (imageFile) {
        // Delete old image if it exists
        if (existingData.imagePath) {
          await this.deleteImage(existingData.imagePath);
        }
        
        // Upload new image
        imageData = await this.uploadImage(imageFile, productId);
      }

      const updatedProduct = {
        ...productData,
        image: imageData ? imageData.url : (productData.image || existingData.image),
        imagePath: imageData ? imageData.path : (productData.imagePath || existingData.imagePath),
        priceValue: parseFloat(productData.priceValue) || existingData.priceValue,
        rating: parseFloat(productData.rating) || existingData.rating,
        reviews: parseInt(productData.reviews) || existingData.reviews,
        inStock: productData.inStock !== undefined ? productData.inStock : existingData.inStock,
        featured: productData.featured !== undefined ? productData.featured : existingData.featured,
        updatedAt: new Date(),
        createdAt: existingData.createdAt // Preserve original creation date
      };

      await updateDoc(docRef, updatedProduct);
      
      return { 
        id: productId, 
        ...updatedProduct 
      };
    } catch (error) {
      console.error('Error updating product:', error);
      
      if (error.code === 'permission-denied') {
        throw new Error('Permission denied. Please sign in as an admin and ensure Firestore security rules are configured correctly.');
      } else if (error.code === 'not-found') {
        throw new Error('Product not found.');
      } else if (error.message) {
        throw new Error(`Failed to update product: ${error.message}`);
      } else {
        throw new Error(`Failed to update product: ${error.code || 'Unknown error'}`);
      }
    }
  }

  // Delete product
  static async deleteProduct(productId) {
    try {
      const docRef = doc(db, COLLECTION_NAME, productId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Product not found');
      }

      const productData = docSnap.data();
      
      // Delete associated image if it exists
      if (productData.imagePath) {
        await this.deleteImage(productData.imagePath);
      }

      // Delete the document
      await deleteDoc(docRef);
      
      return true;
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  }

  // Bulk update products
  static async bulkUpdateProducts(updates) {
    try {
      const batch = writeBatch(db);
      
      updates.forEach(({ id, data }) => {
        const docRef = doc(db, COLLECTION_NAME, id);
        batch.update(docRef, {
          ...data,
          updatedAt: new Date()
        });
      });

      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error bulk updating products:', error);
      throw error;
    }
  }

  // Toggle product stock status
  static async toggleProductStock(productId) {
    try {
      const docRef = doc(db, COLLECTION_NAME, productId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Product not found');
      }

      const currentStock = docSnap.data().inStock;
      
      await updateDoc(docRef, {
        inStock: !currentStock,
        updatedAt: new Date()
      });

      return !currentStock;
    } catch (error) {
      console.error('Error toggling product stock:', error);
      throw error;
    }
  }

  // Toggle featured status
  static async toggleFeaturedStatus(productId) {
    try {
      const docRef = doc(db, COLLECTION_NAME, productId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Product not found');
      }

      const currentFeatured = docSnap.data().featured || false;
      
      await updateDoc(docRef, {
        featured: !currentFeatured,
        updatedAt: new Date()
      });

      return !currentFeatured;
    } catch (error) {
      console.error('Error toggling featured status:', error);
      throw error;
    }
  }

  // Get product statistics
  static async getProductStats() {
    try {
      const products = await this.getAllProductsAdmin();
      
      const stats = {
        total: products.length,
        inStock: products.filter(p => p.inStock).length,
        outOfStock: products.filter(p => !p.inStock).length,
        featured: products.filter(p => p.featured).length,
        categories: {},
        averagePrice: 0,
        totalValue: 0
      };

      // Calculate category counts and price stats
      let totalPrice = 0;
      products.forEach(product => {
        // Category stats
        const category = product.category || 'uncategorized';
        stats.categories[category] = (stats.categories[category] || 0) + 1;
        
        // Price stats
        totalPrice += product.priceValue || 0;
      });

      stats.averagePrice = products.length > 0 ? totalPrice / products.length : 0;
      stats.totalValue = totalPrice;

      return stats;
    } catch (error) {
      console.error('Error getting product stats:', error);
      throw error;
    }
  }

  // Search products (admin version with more fields)
  static async searchProductsAdmin(searchTerm) {
    try {
      const products = await this.getAllProductsAdmin();
      
      return products.filter(product =>
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  }

  // Sync local data to Firebase (one-time migration)
  static async syncLocalDataToFirebase(localData) {
    try {
      const batch = writeBatch(db);
      const timestamp = new Date();
      
      Object.values(localData).forEach((productData) => {
        const docRef = doc(collection(db, COLLECTION_NAME));
        batch.set(docRef, {
          ...productData,
          createdAt: timestamp,
          updatedAt: timestamp,
          inStock: true,
          featured: productData.featured || false
        });
      });

      await batch.commit();
      console.log('Local data synced to Firebase successfully');
      return true;
    } catch (error) {
      console.error('Error syncing local data to Firebase:', error);
      throw error;
    }
  }
}